import { useCallback, useEffect, useState } from "react";

export type HudWidgetKey =
  | "speed"
  | "vspeed"
  | "distance"
  | "compass"
  | "battery"
  | "gmeter"
  | "horizon"
  | "inputs"
  | "minimap"
  | "weather";

export interface HudConfig {
  speed: boolean;
  vspeed: boolean;
  distance: boolean;
  compass: boolean;
  battery: boolean;
  gmeter: boolean;
  horizon: boolean;
  inputs: boolean;
  minimap: boolean;
  weather: boolean;
}

const STORAGE_KEY = "replay.hud.config.v1";

const DEFAULT_CONFIG: HudConfig = {
  speed: true,
  vspeed: true,
  distance: true,
  compass: true,
  battery: true,
  gmeter: false,
  horizon: true,
  inputs: false,
  minimap: true,
  weather: true,
};

function readStorage(): HudConfig {
  if (typeof window === "undefined") return DEFAULT_CONFIG;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_CONFIG;
    const parsed = JSON.parse(raw) as Partial<HudConfig>;
    return { ...DEFAULT_CONFIG, ...parsed };
  } catch {
    return DEFAULT_CONFIG;
  }
}

export function useHudConfig() {
  const [config, setConfig] = useState<HudConfig>(readStorage);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch {
      /* ignore */
    }
  }, [config]);

  const toggle = useCallback((key: HudWidgetKey) => {
    setConfig((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const reset = useCallback(() => setConfig(DEFAULT_CONFIG), []);

  return { config, toggle, reset, setConfig };
}

export const HUD_WIDGET_LABELS: Record<HudWidgetKey, string> = {
  speed: "Speed",
  vspeed: "Vertical speed",
  distance: "Distance",
  compass: "Compass",
  battery: "Battery",
  gmeter: "G-meter",
  horizon: "Artificial horizon",
  inputs: "Stick inputs",
  minimap: "2D minimap",
  weather: "Weather",
};
